import React, {useEffect, useState} from "react";
import {Alert, Button, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag, message} from "antd";
import {DeleteOutlined, EditOutlined, PlusOutlined} from "@ant-design/icons";
import {ApiLinksService} from "../../../../../shared/api";
import {errorOf} from "./linkParts";

// a site: its address, its Metrika counter and goals (from the token's counters), short links on or off
const DomainModal = ({domain, onClose, onSaved}) => {
  const [form] = Form.useForm()
  const [counters, setCounters] = useState(null)
  const [goals, setGoals] = useState([])
  const [saving, setSaving] = useState(false)
  const counter = Form.useWatch('metrika_counter', form)

  useEffect(() => {
    ApiLinksService.fetchCounters()
      .then(({data}) => setCounters(data))
      .catch((e) => {
        setCounters([])
        message.error(errorOf(e).message || 'Метрика не ответила')
      })
  }, [])

  useEffect(() => {
    setGoals([])
    if (!counter) return
    ApiLinksService.fetchGoals(counter)
      .then(({data}) => setGoals(data))
      .catch((e) => message.error(errorOf(e).message || 'Не удалось получить цели счётчика'))
  }, [counter])

  const submit = async () => {
    let v
    try {
      v = await form.validateFields()
    } catch (e) {
      return
    }
    setSaving(true)
    try {
      await ApiLinksService.saveDomain(domain && domain.id, {...v, short_links: Boolean(v.short_links)})
      message.success('Сохранено')
      onSaved()
    } catch (e) {
      const {message: text, field} = errorOf(e)
      if (field) form.setFields([{name: field, errors: [text]}])
      else message.error(text || 'Не удалось сохранить')
    }
    setSaving(false)
  }

  const goalOptions = goals.map((g) => ({value: g.id, label: g.name}))

  return (
    <Modal visible width={620} title={domain ? domain.host : 'Новый сайт'} okText='Сохранить' cancelText='Отмена'
      onOk={submit} confirmLoading={saving} onCancel={onClose} destroyOnClose>
      <Form form={form} layout='vertical' initialValues={{
        host: domain ? domain.host : '',
        title: domain ? domain.title : '',
        metrika_counter: domain && domain.metrika_counter ? Number(domain.metrika_counter) : undefined,
        goal_login: domain && domain.goal_login ? Number(domain.goal_login) : undefined,
        goal_register: domain && domain.goal_register ? Number(domain.goal_register) : undefined,
        short_links: domain ? domain.short_links : false,
        short_base: domain ? domain.short_base || '' : '',
      }}>
        <Form.Item name='host' label='Домен' rules={[{required: true, message: 'Например eci-spb.ru'}]}
          extra={domain && domain.links ? 'По сайту есть ссылки: домен не меняется' : null}>
          <Input placeholder='eci-spb.ru' disabled={Boolean(domain && domain.links)}/>
        </Form.Item>
        <Form.Item name='title' label='Название'>
          <Input maxLength={100} placeholder='ЕЦИ (старый сайт)'/>
        </Form.Item>
        <Form.Item name='metrika_counter' label='Счётчик Яндекс Метрики' extra='Из этого счётчика берётся статистика ссылок на сайт'>
          <Select allowClear loading={counters === null} placeholder='Не подключён'
            options={(counters || []).map((c) => ({value: c.id, label: `${c.site} — ${c.name} (${c.id})`}))}
            onChange={() => form.setFieldsValue({goal_login: undefined, goal_register: undefined})}/>
        </Form.Item>
        <Space style={{display: 'flex'}} align='start'>
          <Form.Item name='goal_login' label='Цель «вход»' style={{width: 270}}>
            <Select allowClear disabled={!counter} options={goalOptions} placeholder='Не считать'/>
          </Form.Item>
          <Form.Item name='goal_register' label='Цель «регистрация»' style={{width: 270}}>
            <Select allowClear disabled={!counter} options={goalOptions} placeholder='Не считать'/>
          </Form.Item>
        </Space>
        <Form.Item name='short_links' label='Короткие ссылки на этот сайт' valuePropName='checked'
          extra='Короткая ссылка перенаправляет на сайт через наш сервер, он считает клики'>
          <Switch/>
        </Form.Item>
        <Form.Item name='short_base' label='Адрес коротких ссылок'
          extra='Пусто — portalagentam.ru/s/имя. Свой адрес на домене сайта (например https://eci-spb.ru/brokers) работает, только если на сайте стоит правило, которое отправляет /brokers/имя на portalagentam.ru/s/имя'>
          <Input placeholder='https://portalagentam.ru/s'/>
        </Form.Item>
      </Form>
    </Modal>
  )
}

// «Сайты»: where links may lead
export const DomainsTab = ({domains, canEdit, onChanged}) => {
  const [editing, setEditing] = useState(null) // {domain} | {domain: null}

  const remove = async (domain) => {
    try {
      await ApiLinksService.removeDomain(domain.id)
      message.success('Удалено')
      onChanged()
    } catch (e) {
      message.error(errorOf(e).message || 'Не удалось удалить')
    }
  }

  const columns = [
    {title: 'Сайт', key: 'host', render: (d) => <><b>{d.host}</b>{d.title && <div className='cell-hint'>{d.title}</div>}</>},
    {title: 'Метрика', key: 'metrika', render: (d) => (d.metrika_counter ? <Tag color='green'>счётчик {d.metrika_counter}</Tag> : <Tag>не подключена</Tag>)},
    {
      title: 'Цели',
      key: 'goals',
      render: (d) => (
        <div className='tag-list'>
          {d.goal_login ? <Tag>вход</Tag> : null}
          {d.goal_register ? <Tag>регистрация</Tag> : null}
          {!d.goal_login && !d.goal_register && <span className='cell-empty'>—</span>}
        </div>
      ),
    },
    {
      title: 'Короткие ссылки',
      key: 'short',
      render: (d) => (d.short_links ? <span>{(d.short_base || 'https://portalagentam.ru/s').replace('https://', '')}/…</span> : 'нет'),
    },
    {title: 'Ссылок', key: 'links', align: 'right', render: (d) => d.links},
    canEdit && {
      title: '',
      key: 'actions',
      width: 110,
      render: (d) => (
        <Space>
          <Button icon={<EditOutlined/>} title='Изменить' onClick={() => setEditing({domain: d})}/>
          {!d.links && (
            <Popconfirm title={`Удалить ${d.host}?`} okText='Удалить' okButtonProps={{danger: true}} cancelText='Нет' onConfirm={() => remove(d)}>
              <Button danger icon={<DeleteOutlined/>} title='Удалить'/>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ].filter(Boolean)

  return (
    <>
      <Alert
        className='section-note'
        type='info'
        showIcon
        message='Сайты, на которые ведут ссылки. Для статистики выберите счётчик Метрики сайта и его цели «вход» и «регистрация». Короткие ссылки идут через наш сервер — включайте их, только если перенаправление на сайт допустимо. Сайты добавляет и настраивает администратор.'
      />
      <Table
        rowKey='id'
        columns={columns}
        dataSource={domains}
        pagination={false}
        title={canEdit ? () => (
          <div className='section-toolbar'>
            <div className='section-toolbar__spacer'/>
            <Button type='primary' icon={<PlusOutlined/>} onClick={() => setEditing({domain: null})}>Добавить сайт</Button>
          </div>
        ) : undefined}
      />
      {editing && <DomainModal domain={editing.domain} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged() }}/>}
    </>
  )
}
